/**
 * axios 封装：自动注入 apiToken、解包 { code, data }、处理登录失效（code=300）。
 * 用法：import request from '@/utils/request' 或 this.$request
 * rawResponse: true 返回完整 axios 响应；skipToken: true 不附带 token。
 */
import axios from 'axios';

const FORM_CONTENT_TYPE = 'application/x-www-form-urlencoded';

/** indiv 新 REST 接口前缀（完整路径，需 baseURL 置空） */
const INDIV_API_PREFIX = '/api/user/api';

/** @type {(() => void) | null} */
let onUnauthorized = null;

/**
 * 初始化全局配置（在 main.js 调用一次）
 * @param {{ onUnauthorized?: () => void }} options
 */
export function setupRequest(options = {}) {
  onUnauthorized = options.onUnauthorized || null;
}

/** 从 localStorage 读取 apiToken */
export function getApiToken() {
  try {
    const token = JSON.parse(localStorage.getItem('token') || '{}');
    return token.apiToken || '';
  } catch (e) {
    return '';
  }
}

function formUrlEncode(data) {
  if (data == null || typeof data !== 'object') {
    return data;
  }
  let ret = '';
  for (const key in data) {
    if (Object.prototype.hasOwnProperty.call(data, key)) {
      ret += `${encodeURIComponent(key)}=${encodeURIComponent(data[key])}&`;
    }
  }
  return ret;
}

/** 是否为 indiv REST 完整路径 */
function isIndivRestUrl(url) {
  return typeof url === 'string' && url.indexOf(INDIV_API_PREFIX) === 0;
}

/** token 是否应走 query params（indiv REST 的 GET/POST 均走 params） */
function shouldTokenInParams(config) {
  if (config.tokenInParams === true) return true;
  if (config.tokenInParams === false) return false;
  return isIndivRestUrl(config.url);
}

function getResponseErrorMessage(res) {
  const body = res && res.data;
  if (!body) return '请求失败';
  return body.message || body.msg || '请求失败';
}

const request = axios.create({
  baseURL: '/api',
  timeout: 60000,
  headers: { 'Content-Type': FORM_CONTENT_TYPE },
  transformRequest: [formUrlEncode],
});

request.interceptors.request.use(
  (config) => {
    // indiv REST：完整路径，baseURL 置空避免拼成 /api/api/user/...
    if (isIndivRestUrl(config.url)) {
      config.baseURL = '';
    }

    if (config.skipToken) {
      return config;
    }

    const apiToken = getApiToken();
    if (!apiToken) {
      return config;
    }

    // 调用方已手动传 token 则不覆盖
    if (config.params && config.params.apiToken) {
      return config;
    }
    if (config.data && typeof config.data === 'object' && config.data.apiToken) {
      return config;
    }

    if (shouldTokenInParams(config)) {
      config.params = Object.assign({ apiToken }, config.params || {});
    } else if (config.method === 'post' || config.method === 'put' || config.method === 'patch') {
      config.data = Object.assign({ apiToken }, config.data || {});
    } else {
      config.params = Object.assign({ apiToken }, config.params || {});
    }

    return config;
  },
  (error) => Promise.reject(error)
);

request.interceptors.response.use(
  (response) => {
    const body = response.data;

    // 登录失效 code=300
    if (body && Number(body.code) === 300) {
      if (onUnauthorized) {
        onUnauthorized();
      }
      return Promise.reject(new Error(body.message || body.msg || '当前登录已失效'));
    }

    // 兼容老写法：返回完整 axios response
    if (response.config.rawResponse) {
      return response;
    }

    // 返回完整业务 body { code, data, message }
    if (response.config.returnBody) {
      return body;
    }

    // 标准 { code, data } — 成功解包 data，失败 reject
    if (body && body.code != null) {
      if (Number(body.code) === 200) {
        return body.data !== undefined ? body.data : null;
      }
      return Promise.reject(new Error(getResponseErrorMessage(response)));
    }

    // 无 code 字段的非标准响应，原样返回 body
    return body;
  },
  (error) => {
    if (error.response) {
      return Promise.reject(new Error(getResponseErrorMessage(error.response)));
    }
    return Promise.reject(new Error(error.message || '网络异常，请稍后重试'));
  }
);

export default request;



// WEBPACK FOOTER //
// ./src/utils/request.js